import { useState, useCallback } from 'react';
import UserService from '../views/User/service';
import { toast } from 'react-toastify';

/**
 * Hook personalizado para gerenciar informações dos membros de grupos de chat
 *
 * Este hook fornece funcionalidades para buscar, cachear e gerenciar informações
 * dos usuários que participam de grupos de chat, incluindo nomes e fotos de perfil.
 *
 * @returns {Object} Objeto contendo funções e estado para gerenciar membros
 * @returns {Function} getMemberInfo - Retorna informações de um membro específico
 * @returns {Function} fetchMemberInfo - Busca informações de um membro da API
 * @returns {Function} preloadMembers - Pré-carrega informações de múltiplos membros
 * @returns {boolean} loading - Estado de carregamento
 * @returns {Map} membersCache - Cache das informações dos membros
 *
 * @example
 * ```jsx
 * import { useGroupMembers } from 'hooks/useGroupMembers';
 *
 * function ChatComponent() {
 *   const { getMemberInfo, preloadMembers, loading } = useGroupMembers();
 *
 *   useEffect(() => {
 *     // Pré-carregar informações de todos os membros do grupo
 *     preloadMembers(['user1', 'user2', 'user3']);
 *   }, []);
 *
 *   const memberName = getMemberInfo('user1').name;
 *
 *   return (
 *     <div>
 *       {loading ? 'Carregando...' : `Nome: ${memberName}`}
 *     </div>
 *   );
 * }
 * ```
 */
export function useGroupMembers() {
  const [membersCache, setMembersCache] = useState(new Map());
  const [loading, setLoading] = useState(false);

  /**
   * Busca informações de um membro específico da API
   * @param {string} login - Login do usuário
   * @returns {Promise<Object>} Informações do membro
   */
  const fetchMemberInfo = useCallback(
    async (login) => {
      if (membersCache.has(login)) {
        return membersCache.get(login);
      }

      try {
        setLoading(true);
        const response = await UserService.get(login);
        const userData = response.data.data;

        const memberInfo = {
          login: userData.login,
          name: userData.name || userData.nome || userData.full_name || login,
          profile_pic: userData.profile_pic
        };

        setMembersCache((prev) => new Map(prev).set(login, memberInfo));

        return memberInfo;
      } catch (error) {
        toast.error(`Erro ao buscar informações do usuário ${login}`, {
          autoClose: 3000,
          closeOnClick: true
        });
        console.error(`Erro ao buscar informações do usuário ${login}:`, error);
        const fallbackInfo = {
          login,
          name: login,
          profile_pic: null
        };

        setMembersCache((prev) => new Map(prev).set(login, fallbackInfo));
        return fallbackInfo;
      } finally {
        setLoading(false);
      }
    },
    [membersCache]
  );

  /**
   * Retorna informações de um membro (do cache ou fallback)
   * @param {string} login - Login do usuário
   * @returns {Object} Informações do membro
   */
  const getMemberInfo = useCallback(
    (login) => {
      return (
        membersCache.get(login) || { login, name: login, profile_pic: null }
      );
    },
    [membersCache]
  );

  /**
   * Pré-carrega informações de múltiplos membros simultaneamente
   * @param {Array<string>} logins - Array de logins dos usuários
   * @returns {Promise<void>}
   */
  const preloadMembers = useCallback(
    async (logins) => {
      if (!Array.isArray(logins) || logins.length === 0) return;

      const uniqueLogins = [...new Set(logins)];
      const promises = uniqueLogins.map((login) => fetchMemberInfo(login));

      try {
        await Promise.all(promises);
      } catch (error) {
        console.error('Erro ao pré-carregar informações dos membros:', error);
      }
    },
    [fetchMemberInfo]
  );

  return {
    getMemberInfo,
    fetchMemberInfo,
    preloadMembers,
    loading,
    membersCache
  };
}
